import React from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  FaWhatsapp,
  FaPhoneAlt,
  FaCommentDots,
  FaClock,
  FaUserMd,
  FaShieldAlt,
  FaRegSmile,
  FaQuestionCircle,
} from "react-icons/fa";
import Button from "../components/common/Button";
import styles from "./Telemedicine.module.css";
import checkUpImage from "../././assets/telemedicine.jpg";

const fadeInUp = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.2 },
  },
};

const Telemedicine = () => {
  const stats = [
    { number: "24/7", label: "Availability", icon: FaClock, color: "#2A7FBA" },
    {
      number: "10 min",
      label: "Avg Response",
      icon: FaUserMd,
      color: "#4CAF50",
    },
    {
      number: "100%",
      label: "Confidential",
      icon: FaShieldAlt,
      color: "#FF9800",
    },
    {
      number: "98%",
      label: "Satisfaction",
      icon: FaRegSmile,
      color: "#9C27B0",
    },
  ];

  const benefits = [
    "Consult from anywhere, anytime",
    "No waiting rooms – get quick advice",
    "Secure and confidential conversations",
    "Prescriptions and referrals sent electronically",
    "Follow‑up care and monitoring",
  ];

  const faqs = [
    {
      q: "How do I start a WhatsApp consultation?",
      a: "Click the 'Start WhatsApp Chat' button above. It will open WhatsApp with a pre‑filled message. Just send your query and the doctor will respond as soon as possible.",
    },
    {
      q: "Is telemedicine secure?",
      a: "Yes, all communications are confidential. We adhere to strict medical privacy standards. For WhatsApp, we recommend using the app's end‑to‑end encryption, and we never store your personal data without consent.",
    },
    {
      q: "What can I consult about?",
      a: "General health questions, medication reviews, follow‑up appointments, test results interpretation, and non‑emergency symptoms. For emergencies, please call emergency services.",
    },
    {
      q: "What are the costs?",
      a: "Telemedicine consultations are charged at the same rate as in‑person visits. We'll provide details before your consultation.",
    },
  ];

  return (
    <>
      <Helmet>
        <title>Telemedicine | Muigai Medical Center</title>
        <meta
          name="description"
          content="Consult with Dr. Jack Kimeu from anywhere via WhatsApp, phone, or live chat. Fast, secure, and convenient healthcare at your fingertips."
        />
        <meta
          name="keywords"
          content="telemedicine, online doctor, WhatsApp consultation, virtual healthcare, remote consultation"
        />
        <meta
          property="og:title"
          content="Telemedicine – Healthcare at Your Fingertips"
        />
        <meta
          property="og:description"
          content="Consult with Dr. Jack Kimeu from anywhere. Secure and convenient virtual care."
        />
      </Helmet>

      {/* Hero Banner */}
      <section
        style={{ backgroundImage: `url(${checkUpImage})` }}
        className={styles.heroBanner}
      >
        <div className={styles.heroOverlay}></div>
        <div className="container">
          <motion.div
            className={styles.heroContent}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <motion.div className={styles.sectionTag} variants={fadeInUp}>
              TELEMEDICINE
            </motion.div>
            <motion.h1 variants={fadeInUp}>
              Healthcare at Your Fingertips
            </motion.h1>
            <motion.p className={styles.heroSubtitle} variants={fadeInUp}>
              Consult with Dr. Jack Kimeu from anywhere, anytime – via WhatsApp,
              phone, or live chat.
            </motion.p>
          </motion.div>
        </div>
        <div className={styles.heroBackground}>
          <div
            className={styles.floatingCircle}
            style={{ top: "15%", left: "10%", width: "120px", height: "120px" }}
          />
          <div
            className={styles.floatingCircle}
            style={{
              bottom: "20%",
              right: "15%",
              width: "80px",
              height: "80px",
            }}
          />
          <div
            className={styles.floatingCircle}
            style={{ top: "60%", left: "20%", width: "60px", height: "60px" }}
          />
        </div>
      </section>

      {/* Stats Section */}
      <motion.section
        className={styles.statsSection}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={staggerContainer}
      >
        <div className="container">
          <div className={styles.statsGrid}>
            {stats.map((stat, idx) => (
              <motion.div
                key={idx}
                className={styles.statCard}
                variants={{
                  hidden: { opacity: 0, y: 30 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.5, delay: idx * 0.1 },
                  },
                }}
                whileHover={{ y: -8 }}
              >
                <div className={styles.statIcon} style={{ color: stat.color }}>
                  <stat.icon />
                </div>
                <div className={styles.statNumber}>{stat.number}</div>
                <div className={styles.statLabel}>{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Methods Section */}
      <section className={styles.methodsSection}>
        <div className="container">
          <h2 className={styles.methodsTitle}>
            Choose Your Consultation Method
          </h2>
          <div className={styles.methodsGrid}>
            <div className={styles.methodCard}>
              <div className={styles.methodIcon}>
                <FaWhatsapp />
              </div>
              <h3>WhatsApp Consultation</h3>
              <p>
                Chat directly with the doctor via WhatsApp. Send messages,
                photos, and get quick responses.
              </p>
              <Button
                href="https://wa.me/254722265791?text=Hello,%20I%20would%20like%20an%20online%20consultation"
                variant="whatsapp-tele"
              >
                Start WhatsApp Chat
              </Button>
            </div>
            <div className={styles.methodCard}>
              <div className={styles.methodIcon}>
                <FaPhoneAlt />
              </div>
              <h3>Phone Consultation</h3>
              <p>
                Call us directly for a voice consultation. Our lines are open
                during clinic hours.
              </p>
              <Button href="tel:+254722265791" variant="primary-tele">
                Call Now
              </Button>
            </div>
            <div className={styles.methodCard}>
              <div className={styles.methodIcon}>
                <FaCommentDots />
              </div>
              <h3>Live Chat (Coming Soon)</h3>
              <p>
                Our website chat will be available shortly for instant messaging
                with our team.
              </p>
              <Button disabled variant="secondary">
                Live Chat
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className={styles.benefitsSection}>
        <div className="container">
          <div className={styles.benefitsGrid}>
            <div className={styles.benefitsContent}>
              <h2>Why Choose Telemedicine?</h2>
              <ul>
                {benefits.map((benefit, idx) => (
                  <li key={idx}>
                    <span className={styles.checkmark}>✓</span>
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>
            <div className={styles.benefitsImage}>
              <img
                src="/assets/telemedicine.jpg"
                alt="Telemedicine consultation"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className={styles.faqSection}>
        <div className="container">
          <h2>Frequently Asked Questions</h2>
          <div className={styles.faqGrid}>
            {faqs.map((faq, idx) => (
              <div key={idx} className={styles.faqItem}>
                <div className={styles.faqQuestion}>
                  <FaQuestionCircle className={styles.faqIcon} />
                  <h3>{faq.q}</h3>
                </div>
                <p className={styles.faqAnswer}>{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Privacy Notice */}
      <section className={styles.privacySection}>
        <div className="container">
          <div className={styles.privacyCard}>
            <FaShieldAlt className={styles.privacyIcon} />
            <h3>Your Privacy Matters</h3>
            <p>
              All communications are confidential. We adhere to strict medical
              privacy standards. For emergency medical situations, please call
              emergency services immediately.
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default Telemedicine;
